import { useEffect, useRef, useState } from 'react';

/**
 * Generic IntersectionObserver wrapper.
 *
 * Attach the returned `ref` to the element you want to observe and read
 * `inView`. The value latches: it flips to `true` on the first intersection and
 * stays `true` (one-shot trigger). Inert when IntersectionObserver is
 * unavailable (e.g. jsdom under Jest).
 */
export const useInView = <T extends Element>(options?: IntersectionObserverInit) => {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        setInView(true);
      }
    }, options);

    observer.observe(el);

    return () => observer.disconnect();
    // options intentionally omitted: the observer is created once on mount and
    // the latch is one-shot, so re-creating on option identity changes is
    // unnecessary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ref, inView };
};
